import { useState, useEffect, useRef, useContext } from 'react';
import io from 'socket.io-client';
import { AuthContext } from '../context/AuthContext';
import trackingApi from '../api/v1/trackingApi';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3000';

/**
 * Hook personalizado para manejar ubicaciones de trabajadores en tiempo real
 *
 * Conecta a Socket.IO y mantiene sincronizadas las ubicaciones de todos los trabajadores
 *
 * @returns {Object} - Estado de conexión, trabajadores y funciones
 */
export const useWorkerLocations = () => {
  const { user } = useContext(AuthContext);
  const [workers, setWorkers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!user || !token) {
      setIsLoading(false);
      return;
    }

    // Cargar ubicaciones iniciales desde la API
    const loadInitialLocations = async () => {
      try {
        setIsLoading(true);
        const response = await trackingApi.getCurrentWorkerLocations();
        setWorkers(response.data || []);
        setError(null);
      } catch (err) {
        console.error('❌ Error cargando ubicaciones iniciales:', err);
        setError('Error al cargar ubicaciones de trabajadores');
        setWorkers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialLocations();

    // Conectar a Socket.IO
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    // Eventos de conexión
    socket.on('connect', () => {
      console.log('✅ Conectado a Socket.IO para tracking');
      setIsConnected(true);

      // Unirse a la sala de la empresa del usuario
      if (user.id_empresa) {
        socket.emit('joinCompany', user.id_empresa);
        console.log(`📍 Unido a company_${user.id_empresa}`);
      }
    });

    socket.on('disconnect', () => {
      console.log('❌ Desconectado de Socket.IO');
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('❌ Error de conexión Socket.IO:', err);
      setIsConnected(false);
    });

    // Evento cuando llega una actualización de ubicación
    socket.on('location-update', (data) => {
      console.log('📍 Ubicación actualizada:', data);

      setWorkers(prev => {
        // Buscar si el trabajador ya existe
        const index = prev.findIndex(w => w.id_trabajador === data.id_trabajador);

        if (index !== -1) {
          // Actualizar trabajador existente
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            ...data,
            segundos_desde_actualizacion: 0,
            estado: 'online'
          };
          return updated;
        } else {
          // Agregar nuevo trabajador
          return [...prev, {
            ...data,
            segundos_desde_actualizacion: 0,
            estado: 'online'
          }];
        }
      });
    });

    // Evento general (por si acaso)
    socket.on('worker-location-update', (data) => {
      // Este es un evento de respaldo, la lógica principal está en 'location-update'
      console.log('📍 Worker location update (general):', data);
    });

    // Cleanup al desmontar
    return () => {
      if (user && user.id_empresa) {
        socket.emit('leaveCompany', user.id_empresa);
      }
      socket.close();
    };
  }, [user]);

  // Actualizar segundos_desde_actualizacion cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setWorkers(prev =>
        prev.map(worker => {
          const segundos = worker.segundos_desde_actualizacion + 10;

          // Calcular estado según segundos desde última actualización
          let estado = 'offline';
          if (segundos < 60) {
            estado = 'online';
          } else if (segundos < 300) {
            estado = 'idle';
          }

          return {
            ...worker,
            segundos_desde_actualizacion: segundos,
            estado
          };
        })
      );
    }, 10000); // Cada 10 segundos

    return () => clearInterval(interval);
  }, []);

  /**
   * Forzar recarga de ubicaciones desde el servidor
   */
  const refreshLocations = async () => {
    try {
      setIsLoading(true);
      const response = await trackingApi.getCurrentWorkerLocations();
      setWorkers(response.data || []);
      setError(null);
    } catch (err) {
      console.error('❌ Error recargando ubicaciones:', err);
      setError('Error al recargar ubicaciones de trabajadores');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Obtener trabajador específico
   */
  const getWorkerById = (id_trabajador) => {
    return workers.find(w => w.id_trabajador === id_trabajador);
  };

  /**
   * Filtrar trabajadores por estado
   */
  const getWorkersByStatus = (estado) => {
    return workers.filter(w => w.estado === estado);
  };

  return {
    workers,
    isConnected,
    isLoading,
    error,
    socket: socketRef.current,
    refreshLocations,
    getWorkerById,
    getWorkersByStatus,
    onlineCount: workers.filter(w => w.estado === 'online').length,
    idleCount: workers.filter(w => w.estado === 'idle').length,
    offlineCount: workers.filter(w => w.estado === 'offline').length
  };
};

export default useWorkerLocations;
